import breadCrumbData from '../pages/SeoLocationLandingPage/breadCrumb.json';
import { sportTypeIds } from './eventConstants';

// Clamps the page <title> to maxLen chars by truncating eventName first,
// preserving the keyword-rich suffix intact (Google truncates at ~60 chars).
export const clampTitle = (eventName, suffix, maxLen = 60) => {
    if (!eventName) {
        return suffix ?? '';
    }
    if (!suffix) {
        return eventName;
    }
    const full = `${eventName}${suffix}`;
    if (full.length <= maxLen) {
        return full;
    }
    const budget = maxLen - suffix.length - 1;
    // suffix is a fixed template fragment and is never long enough to exceed maxLen on its own
    return budget > 0 ? `${eventName.slice(0, budget)}…${suffix}` : suffix;
};

export const renderBannerTitle = (primarylocation, secondarylocation, tertiarylocation) => {
    const locationData = breadCrumbData[primarylocation];
    const cityDescriptionTitle = `${locationData[secondarylocation]?.[tertiarylocation]?.displayName}, ${locationData[secondarylocation]?.displayName}`;
    const stateOrProvinceDescriptionTitle = `${locationData[secondarylocation]?.displayName}`;

    if (!locationData) {
        return null;
    }
    if (tertiarylocation) {
        return `${cityDescriptionTitle}`;
    }
    if (secondarylocation && locationData[secondarylocation]) {
        return `${stateOrProvinceDescriptionTitle}`;
    }
    return `${locationData?.displayName}`;
};

export const generateBreadcrumb = (primarylocation, secondarylocation, tertiarylocation) => {
    let currentLocation = breadCrumbData;
    const newBreadcrumbs = [];
    const checkIsValidURL = () => {
        const currentLocation = breadCrumbData;
        let valueToCheck;
        if (tertiarylocation) {
            valueToCheck = currentLocation[primarylocation]?.[secondarylocation]?.[tertiarylocation];
        } else if (secondarylocation) {
            valueToCheck = currentLocation[primarylocation]?.[secondarylocation];
        } else if (primarylocation) {
            valueToCheck = currentLocation[primarylocation];
        } else {
            valueToCheck = null;
        }
        return valueToCheck;
    };

    const isValidUrl = checkIsValidURL();
    if (!isValidUrl) {
        return newBreadcrumbs;
    }
    currentLocation = currentLocation[primarylocation];
    newBreadcrumbs.push({
        name: currentLocation.displayName,
        path: primarylocation,
    });
    if (secondarylocation && currentLocation[secondarylocation]) {
        currentLocation = currentLocation[secondarylocation];
        newBreadcrumbs.push({
            name: currentLocation.displayName,
            path: `${primarylocation}/${secondarylocation}`,
        });
        if (tertiarylocation && currentLocation[tertiarylocation]) {
            currentLocation = currentLocation[tertiarylocation];
            newBreadcrumbs.push({
                name: currentLocation.displayName,
                path: `${primarylocation}/${secondarylocation}/${tertiarylocation}`,
            });
        }
    }
    return newBreadcrumbs;
};

export const generateEventMetaData = (eventData, appConfig) => {
    if (!eventData) {
        return { title: appConfig.defaultTitle, description: '' };
    }
    const city = eventData.eventLocations?.[0]?.cityName;
    const state = eventData.eventLocations?.[0]?.stateAbbr;
    const locationDisplay = eventData.eventLocations?.[0]?.display;
    const locationStr = (city && state) ? `${city}, ${state}` : locationDisplay ?? '';
    const { sportTypeId } = eventData;
    const eventTitle = eventData.name;
    const eventType = eventData.eventTypeDisplayName;
    const sportType = sportTypeId === sportTypeIds.TENNIS ? 'Tennis' : 'Pickleball';

    const eventTypeStr = eventType ? ` ${eventType}` : '';
    const titleSuffix = ` | ${sportType}${eventTypeStr} in ${locationStr}`;
    const title = `${eventTitle}${titleSuffix}`;
    const clampedTitle = clampTitle(eventTitle, titleSuffix);
    const description = `${eventTitle} is a ${sportType.toLowerCase()}${eventTypeStr.toLowerCase()} in ${locationStr}. Register now.`;

    return {
        url: `${appConfig.appHost}/events/${eventData.id}`,
        canonical: `${appConfig.appHost}/events/${eventData.id}`,
        keywords: `UTR Sports events, Find UTR Sports ${sportType.toLowerCase()} events, UTR post score`,
        image: `https://www.${appConfig.siteBaseUrl}/img/utr_sports/utr-sports-logo.svg`,
        imageWidth: '930',
        imageHeight: '395',
        title,
        clampedTitle,
        description,
    };
};

const buildLocation = (eventData) => {
    const city = eventData.eventLocations?.[0]?.cityName;
    const state = eventData.eventLocations?.[0]?.stateAbbr;
    const clubName = eventData.club?.name;
    return {
        '@type': 'Place',
        ...(clubName && { name: clubName }),
        address: {
            '@type': 'PostalAddress',
            ...(city && { addressLocality: city }),
            ...(state && { addressRegion: state }),
        },
    };
};

const buildOrganizer = (eventData, appHost) => {
    const clubName = eventData.club?.name;
    const clubId = eventData.club?.id;
    const url = appHost && clubId ? `${appHost}/clubs/${clubId}` : null;
    return {
        '@type': 'Organization',
        ...(clubName && { name: clubName }),
        ...(url && { url }),
    };
};

const buildOffers = (eventData, appHost) => {
    const registrationStartUtc = eventData.eventSchedule?.registrationStartUtc;
    const registrationEndUtc = eventData.eventSchedule?.registrationEndUtc;
    const currency = eventData.currency?.abbreviation;
    const fees = (eventData.eventDivisions || [])
        .map(d => d.entryFee)
        .filter(fee => fee != null && fee > 0);
    const price = fees.length > 0 ? Math.min(...fees) : 0;
    const isOpen = !registrationEndUtc || new Date(registrationEndUtc) > new Date();
    const url = appHost && eventData.id ? `${appHost}/events/${eventData.id}` : null;
    return {
        '@type': 'Offer',
        price,
        ...(currency && { priceCurrency: currency }),
        ...(url && { url }),
        ...(registrationStartUtc && { validFrom: registrationStartUtc }),
        ...(registrationEndUtc && { validThrough: registrationEndUtc }),
        availability: isOpen ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock',
    };
};

const resolveImage = (eventData, siteBaseUrl) => {
    if (eventData.photoUrl) {
        return eventData.photoUrl;
    }
    return siteBaseUrl ? `https://www.${siteBaseUrl}/img/utr_sports/utr-sports-logo.svg` : null;
};

export const generateEventJsonLd = (eventData, appConfig) => {
    if (!eventData) {
        return null;
    }

    const startDate = eventData.eventSchedule?.eventStartUtc;
    const endDate = eventData.eventSchedule?.eventEndUtc;
    const sportType = eventData.sportTypeId === sportTypeIds.TENNIS ? 'Tennis' : 'Pickleball';
    const image = resolveImage(eventData, appConfig?.siteBaseUrl);

    return {
        '@context': 'https://schema.org',
        '@type': 'SportsEvent',
        name: eventData.name,
        sport: sportType,
        eventStatus: 'https://schema.org/EventScheduled',
        ...(image && { image }),
        ...(startDate && { startDate }),
        ...(endDate && { endDate }),
        location: buildLocation(eventData),
        organizer: buildOrganizer(eventData, appConfig?.appHost),
        offers: buildOffers(eventData, appConfig?.appHost),
    };
};
